import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "sonner";

interface WatchlistContextType {
  watchlist: string[];
  isInWatchlist: (assetId: string) => boolean;
  toggleWatchlist: (assetId: string) => void;
  addToWatchlist: (assetId: string) => void;
  removeFromWatchlist: (assetId: string) => void;
  clearWatchlist: () => void;
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(
  undefined
);

const STORAGE_KEY = "vestn-watchlist";

export const WatchlistProvider = ({ children }: { children: ReactNode }) => {
  // Initialize state with localStorage value (lazy initialization)
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist to localStorage whenever watchlist changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(watchlist));
  }, [watchlist]);

  const addToWatchlist = useCallback((assetId: string) => {
    setWatchlist((prev) => {
      if (prev.includes(assetId)) {
        return prev;
      }
      toast.success("Added to Watchlist", {
        description: "You'll receive updates about this asset.",
      });
      return [...prev, assetId];
    });
  }, []);

  const removeFromWatchlist = useCallback((assetId: string) => {
    setWatchlist((prev) => prev.filter((id) => id !== assetId));
    toast.success("Removed from Watchlist", {
      description: "Asset removed from your watchlist.",
    });
  }, []);

  const toggleWatchlist = useCallback(
    (assetId: string) => {
      if (watchlist.includes(assetId)) {
        removeFromWatchlist(assetId);
      } else {
        addToWatchlist(assetId);
      }
    },
    [watchlist, addToWatchlist, removeFromWatchlist]
  );

  const isInWatchlist = useCallback(
    (assetId: string) => {
      return watchlist.includes(assetId);
    },
    [watchlist]
  );

  const clearWatchlist = useCallback(() => {
    setWatchlist([]);
    toast.success("Watchlist Cleared", {
      description: "All assets removed from your watchlist.",
    });
  }, []);

  return (
    <WatchlistContext.Provider
      value={{
        watchlist,
        isInWatchlist,
        toggleWatchlist,
        addToWatchlist,
        removeFromWatchlist,
        clearWatchlist,
      }}
    >
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error("useWatchlist must be used within a WatchlistProvider");
  }
  return context;
};
